import { useMemo } from 'react'

import { useEntitiesColors } from 'contexts/EntitiesColorsContext'
import { useCopyToClipboard } from 'hooks'
import { customerioTracker } from 'lib'
import { useTranslate } from 'lib/tolgee'
import { FeatureFlagsStore, PanelStore, UserStore } from 'stores'
import { datadog, TOLGEE_GLOBALS } from 'utils'
import { getTerritoryUrlByTerritoryId } from 'utils/getTerritoryUrlByTerritoryId'

import { PARSE_QUERY_ERROR_LOG } from './niche-card.consts'
import { translations } from './niche-card.i18n'
import type { INicheCardModelParams, INicheCardModelReturn } from './niche-card.types'
import { formatParentTerritoryIds, formatTerritoryQueryClause } from './niche-card.utils'
import { NICHE_LIMIT } from '../common/niche.consts'
import { getCategoryUrl, getNicheUrl, getScopedNicheUrl } from '../common/niche.utils'
import { isGlobalTaxonomyTerritoryId, resolveNicheSelectionTerritoryId } from '../common/promoted-niche.utils'

export const useNicheCardModel = ({
  id,
  territoryQuery,
  parentTerritoryIds,
  canCopyTerritoryQuery,
  shouldDisplayLimitAlert,
  isVisible,
  territoryId,
  feature,
  isPromoted,
  promotedTargetId,
  scopedCategoryId,
}: INicheCardModelParams): INicheCardModelReturn => {
  const t = useTranslate()
  const { copyToClipboard } = useCopyToClipboard()
  const { getColorByEntityId } = useEntitiesColors()

  const isCopyTerritoryQueryEnabled = FeatureFlagsStore.isFeatureFlagEnabled('isCopyTerritoryQueryEnabled')
  const isUserViewer = UserStore.getRole === 'viewer'

  // A "niche" card can actually represent a Category (industry_topic): either an
  // item whose own territory is an industry_topic, or a promoted niche that links
  // to the Category it was promoted into. The add-to checkbox must register the
  // canonical territory (shared with create-report) so it is not stored/queried
  // as a regular niche.
  const [checkboxEntityType, checkboxId] = resolveNicheSelectionTerritoryId({
    id,
    territoryId,
    isPromoted,
    promotedTargetId,
  }).split('.')
  const checkboxEntityObjectType = checkboxEntityType === 'niche' ? 'niches' : checkboxEntityType
  const color = useMemo(() => {
    return getColorByEntityId(territoryId, 'topics')
  }, [getColorByEntityId, territoryId])

  const shouldDisplayCopyTerritoryQuery =
    isCopyTerritoryQueryEnabled && canCopyTerritoryQuery && !!territoryQuery && !isUserViewer
  const isCopyTerritoryQueryDisabled =
    !territoryQuery || isUserViewer || !Array.isArray(parentTerritoryIds) || !parentTerritoryIds.length

  const handleCopyTerritoryQuery = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    if (isCopyTerritoryQueryDisabled) return

    const formattedParentTerritoryIds = formatParentTerritoryIds(parentTerritoryIds)
    try {
      const formattedTerritoryQuery = formatTerritoryQueryClause({ territoryQuery, isPromoted, promotedTargetId })

      const fullQuery = `(${formattedParentTerritoryIds}) AND (${formattedTerritoryQuery})`

      copyToClipboard({ textToCopy: fullQuery, successText: t(TOLGEE_GLOBALS.territoryQueryCopied) })
    } catch (error) {
      datadog.dispatchGenericErrorLog({
        log: PARSE_QUERY_ERROR_LOG,
        error: error as Error,
      })
    }
  }

  const handleTrackClick = ({ name }: { name: string }) => {
    customerioTracker.track(customerioTracker.eventNames.CLICK_NICHE, {
      feature,
      action: 'click_page',
      tab: 'niches',
      name,
    })
  }

  const eyeIconTooltipMessage = useMemo(() => {
    if (isVisible) {
      return t(translations.hideNiche)
    }
    return shouldDisplayLimitAlert ? t(translations.nicheLimitMessage(NICHE_LIMIT)) : t(translations.showNiche)
  }, [isVisible, shouldDisplayLimitAlert, t])

  // Promoted niches link to the industry they were promoted into (the promotion
  // `target`, a separate territory), not to the niche's own page; child niches
  // inside a promoted Category page use the scoped route; everything else keeps
  // today's Niche Territory URL. `isPromoted` wins over `scopedCategoryId`.
  const targetUrl = useMemo(() => {
    if (isPromoted && promotedTargetId) {
      const promotedCategoryId = promotedTargetId.split('.')[1] ?? promotedTargetId
      return getCategoryUrl(promotedCategoryId)
    }
    if (isGlobalTaxonomyTerritoryId(territoryId)) {
      const workspaceId = Object(PanelStore?.selectedPanel)?.id
      return `${getTerritoryUrlByTerritoryId(territoryId, workspaceId)}/overview`
    }
    if (scopedCategoryId) return getScopedNicheUrl(scopedCategoryId, id)
    return getNicheUrl(id)
  }, [isPromoted, promotedTargetId, territoryId, scopedCategoryId, id])

  return {
    handleCopyTerritoryQuery,
    isCopyTerritoryQueryDisabled,
    shouldDisplayCopyTerritoryQuery,
    eyeIconTooltipMessage,
    color,
    handleTrackClick,
    targetUrl,
    checkboxEntityType,
    checkboxEntityObjectType,
    checkboxId,
  }
}
